/**
 * Keyboard-reframe diagnostic recorder (founder device debugging, 2026-08-20).
 *
 * The keyboard-open modal bounce is iOS-PWA/Safari-only — nothing in CI or
 * jsdom can observe it, and DevTools aren't reachable on the founder's phone.
 * This module records every actor in the reframe pipeline (raw visualViewport
 * events, the damped frames the hook commits, the paddings the shell applies,
 * the dialog's actual on-screen rect) into a rolling buffer that
 * KeyboardDebugOverlay renders with a "Copy" button, so a jank repro can be
 * pasted into a chat and diagnosed off-device.
 *
 * OFF by default and free in production paths: `kbLog` bails on one cached
 * boolean read. Enable with `?kbdebug=1` on the URL (persists via
 * localStorage for the session's subsequent navigations), disable with
 * `?kbdebug=0`. Never enabled without the explicit flag.
 *
 * DEV BUILDS ONLY (2026-08-24): the flag cannot be turned on in production at
 * all — not by the query param, not by a stale localStorage entry. `?kbdebug=1`
 * still works on localhost AND on `app-dev.crewvex.com`, which is where
 * every iOS-PWA capture is actually taken. See `debugBuild.js` for why that
 * gate is NOT `NODE_ENV` (Vercel builds the dev site with NODE_ENV=production).
 */

import { IS_DEBUG_BUILD } from '../env/debugBuild';

const STORAGE_KEY = 'cvx:kbdebug';
const MAX_ENTRIES = 600;

let cachedEnabled = null;

export function kbDebugEnabled() {
  if (cachedEnabled != null) return cachedEnabled;
  // Production can never enable this — checked ahead of the URL/localStorage
  // reads so neither a typed `?kbdebug=1` nor a stale flag can revive it.
  if (!IS_DEBUG_BUILD) {
    cachedEnabled = false;
    return cachedEnabled;
  }
  if (typeof window === 'undefined') return false;
  try {
    const q = window.location.search || '';
    if (q.includes('kbdebug=0')) {
      window.localStorage.removeItem(STORAGE_KEY);
      cachedEnabled = false;
      return cachedEnabled;
    }
    if (q.includes('kbdebug=1')) {
      window.localStorage.setItem(STORAGE_KEY, '1');
      cachedEnabled = true;
      return cachedEnabled;
    }
    cachedEnabled = window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    cachedEnabled = false;
  }
  return cachedEnabled;
}

const buffer = [];
const listeners = new Set();
let epoch = 0;

function notify() {
  listeners.forEach((fn) => {
    try { fn(buffer.length); } catch { /* listener errors never break logging */ }
  });
}

/**
 * Append one entry. `data` is a flat object of short keys → numbers/strings;
 * rounded to 1dp so the copied log stays compact.
 */
export function kbLog(type, data) {
  if (!kbDebugEnabled()) return;
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  if (!epoch) epoch = now;
  buffer.push({ t: Math.round(now - epoch), type, data });
  if (buffer.length > MAX_ENTRIES) buffer.splice(0, buffer.length - MAX_ENTRIES);
  notify();
}

/** Mark a fresh capture window (called on focusin) so timestamps read small. */
export function kbLogMark() {
  if (!kbDebugEnabled()) return;
  if (buffer.length === 0) epoch = 0;
}

export function kbClear() {
  buffer.length = 0;
  epoch = 0;
  notify();
}

export function kbSubscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function fmtVal(v) {
  if (typeof v === 'number') return String(Math.round(v * 10) / 10);
  return String(v);
}

export function kbFormatLog() {
  const meta = typeof window !== 'undefined'
    ? `# kbdebug ${window.navigator.userAgent}\n# standalone=${window.navigator.standalone === true} innerH=${window.innerHeight} vv=${window.visualViewport ? 'yes' : 'no'}\n`
    : '';
  return meta + buffer
    .map((e) => `+${String(e.t).padStart(5)} ${e.type} ${
      e.data ? Object.entries(e.data).map(([k, v]) => `${k}=${fmtVal(v)}`).join(' ') : ''
    }`)
    .join('\n');
}

export function kbEntryCount() {
  return buffer.length;
}
